import PropTypes from 'prop-types';
import cx from 'classnames';

import withTheme from 'core/hocs/withTheme';

import themePropTypes from 'core/utils/prop-types/theme';
import { reactedReactionAttributes } from 'core/utils/prop-types/reactions';

import Emoji from 'core/components/Icon/Emoji';

import styles from './index.styl';


const EMOJIS = ['fire', 'heart', 'thumbs_up', 'thumbs_down'];

function EmojiReactions(props) {
  const {
    targetId,
    theme,
    type,
    reactions,
    sumbitReactions,
    reactedReaction,
    awaiting,
    emojis,
    emojiSize,
    reactionsClassName,
    labelClassName,
    inputClassName,
    contentClassName,
  } = props;

  const animationsHover = theme.animations.hover;
  const reactionsAtoms = theme.controls.reactions;

  const {
    idle: idleAtoms,
    checked: checkedAtoms,
    focus: focusAtoms,
    disabled: disabledAtoms,
    checkedDisabled: checkedDisabledAtoms,
  } = reactionsAtoms[type];

  const inputProps = {
    name: 'reactions_emoji_' + targetId,
    onChange: sumbitReactions,
    type: 'checkbox',
  };

  return (
    <div className={cx(styles.reactions, reactionsClassName)} data-qa='reactions'>
      <style jsx>{`
        .${styles.input}
          &:checked ~ .${styles.content}
          &:hover:checked ~ .${styles.content}
            color ${checkedAtoms.color}
            border ${checkedAtoms.border}
            background ${checkedAtoms.background}

          &:hover ~ .${styles.content}
          &:focus ~ .${styles.content}
            color ${focusAtoms.color}
            border ${focusAtoms.border}
            background ${focusAtoms.background}

          &:disabled ~ .${styles.content}
            color ${disabledAtoms.color}
            border ${disabledAtoms.border}
            background ${disabledAtoms.background}

          &:disabled:checked ~ .${styles.content}
            color ${checkedDisabledAtoms.color}
            border ${checkedDisabledAtoms.border}
            background ${checkedDisabledAtoms.background}

        .${styles.content}
          color ${idleAtoms.color}
          border ${idleAtoms.border}
          background ${idleAtoms.background}
          font ${reactionsAtoms.small.font}
          padding ${reactionsAtoms.small.padding}
          border-radius ${reactionsAtoms.small.radius}
          transition all ${animationsHover}
      `}</style>

      {emojis.map(emojiSlug => {
        const checked = reactedReaction?.reacted === emojiSlug;
        /**
         * Если пользователь отреагировал, реакцию можно отжать и переголосовать.
         * Но только при наличии meta данных, чтобы произвести запрос.
         */
        const disabled = !!reactedReaction?.meta && !checked;
        const count = reactedReaction?.values?.[emojiSlug] ?? reactions[emojiSlug];

        return (
          <label
            key={emojiSlug}
            className={cx(styles.label, labelClassName)}
          >
            <input
              checked={checked}
              disabled={awaiting || disabled}
              value={emojiSlug}
              className={cx(styles.input, inputClassName)}
              {...inputProps}
            />
            <div className={cx(styles.content, contentClassName)}>
              <Emoji size={emojiSize} type={emojiSlug} />
              {!!count && <span className={styles.count}>{count}</span>}
            </div>
          </label>
        );
      })}
    </div>
  );
}

const sizeContract = `{
  font,
  radius,
  padding,
}`;

const stateContract = `{
  color,
  border,
  background,
}`;

EmojiReactions.propTypes = {
  /**
   * Массив слагов emojies используемых в реакциях
   */
  emojis: PropTypes.arrayOf(PropTypes.string),

  /**
   * Состояние пока обрабатывается запрос.
   */
  awaiting: PropTypes.bool,
  /**
   * В стори, это ID карточки-виджета.
   * В топике это ID топика.
   */
  targetId: PropTypes.string.isRequired,

  /**
   * Реакция в LocalStorage
   */
  reactedReaction: reactedReactionAttributes,

  /**
   * Обработчик события onChange для инпута реакции
   */
  sumbitReactions: PropTypes.func,

  /**
   * Может быть по разному стилизован в стори-виджете и обычном топике.
   */
  type: PropTypes.string,

  /**
   * Может иметь разные размеры в стори-виджете и обычном топике.
   */
  size: PropTypes.string,

  /**
   * Размер икноки emoji
   */
  emojiSize: PropTypes.number,

  /**
   * Счетчики реакций
   */
  reactions: PropTypes.object,

  /**
   * Класс для кастомизации реакций
   */
  reactionsClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  contentClassName: PropTypes.string,

  /** @ignore */
  theme: themePropTypes(`{
    controls: {
      reactions: {
        small: ${sizeContract},
        primary: {
          idle: ${stateContract},
          checked: ${stateContract},
          focus: ${stateContract},
          disabled: ${stateContract},
          checkedDisabled: ${stateContract},
        }
      }
    }
  `),
};

EmojiReactions.defaultProps = {
  type: 'primary',
  size: 'small',
  emojis: EMOJIS,
};

export { default as EmojiLoader } from './Loader';

const EmojiReactionsWithHOC = withTheme(EmojiReactions);
EmojiReactionsWithHOC.displayName = 'EmojiReactions';

export { EmojiReactions as StorybookComponent };
export default EmojiReactionsWithHOC;
